export type ParticipantRole = 'officer' | 'caller' | 'unknown';

/** Browser names cannot borrow LiveKit's internal participant namespaces. */
export const RESERVED_PARTICIPANT_IDENTITY_PREFIXES = [
  'bh-', 'bithuman-', 'agent-', 'transcriber', 'analyzer', 'lk-',
  'sip_', 'eg_', 'ingress',
] as const;

export function hasReservedParticipantIdentityPrefix(identity: string): boolean {
  const lowered = identity.toLowerCase();
  return RESERVED_PARTICIPANT_IDENTITY_PREFIXES.some((prefix) => lowered.startsWith(prefix));
}


/** Exact controller-minted suffix; this parser alone does not authenticate a stored record. */
export function structuralControllerMintedRole(identity: unknown): 'officer' | 'caller' | null {
  if (typeof identity !== 'string' || !identity || hasReservedParticipantIdentityPrefix(identity)) return null;
  const separator = identity.lastIndexOf('__');
  if (separator <= 0) return null;
  const suffix = identity.slice(separator + 2);
  const role = /^(off|cit)_[a-z0-9]{4}$/.exec(suffix)?.[1];
  return role === 'off' ? 'officer' : role === 'cit' ? 'caller' : null;
}

/** LiveKit kind and lk.agent.name are server-assigned; names and metadata are excluded. */
export interface AttestedRoleParticipant {
  identity?: string | null;
  kind?: unknown;
  attributes?: Record<string, string> | null;
  name?: string | null;
  metadata?: string | null;
}

export const OFFICER_ASSIST_DISPATCH_NAME_PATTERN = /^officer-assist[a-z0-9-]{0,40}$/;

export function isAttestedOfficerAssist(participant: AttestedRoleParticipant | null | undefined): boolean {
  const agentName = participant?.attributes?.['lk.agent.name'];
  return participant?.kind === 4 &&
    typeof agentName === 'string' &&
    OFFICER_ASSIST_DISPATCH_NAME_PATTERN.test(agentName);
}

export function attestedLiveParticipantRole(
  participant: AttestedRoleParticipant | null | undefined,
): ParticipantRole {
  if (!participant) return 'unknown';
  if (participant.kind === 0) return structuralControllerMintedRole(participant.identity) ?? 'unknown';
  if (participant.kind === 3) return 'caller';
  if (participant.kind === 4 && isAttestedOfficerAssist(participant)) return 'officer';
  return 'unknown';
}

export function isAttestedHumanOfficer(participant: AttestedRoleParticipant | null | undefined): boolean {
  return participant?.kind === 0 && structuralControllerMintedRole(participant.identity) === 'officer';
}

const STAFF_ROLES = new Set(['officer', 'admin', 'supervisor']);
const CALLER_ROLES = new Set(['caller', 'citizen', 'civilian']);
const ADMIN_PLATFORM_ROLES = new Set(['admin-platform', 'admin-tenant']);

export function parseParticipantMetadata(metadata?: string | null): Record<string, unknown> | null {
  if (!metadata) {
    return null;
  }

  try {
    const parsed = JSON.parse(metadata);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export function getParticipantRole({
  identity,
  name,
  metadata,
}: {
  identity?: string | null;
  name?: string | null;
  metadata?: string | null;
  // Plumbed by live callers during Phase A; legacy helper behavior is unchanged.
  kind?: unknown;
  attributes?: Record<string, string> | null;
}): ParticipantRole {
  const identityValue = (identity || '').toLowerCase();
  const nameValue = (name || '').toLowerCase();

  const parsed = parseParticipantMetadata(metadata);
  const roleValue = typeof parsed?.role === 'string' ? parsed.role.toLowerCase() : '';
  const platformRoleValue = typeof parsed?.platform_role === 'string' ? parsed.platform_role.toLowerCase() : '';

  if (STAFF_ROLES.has(roleValue) || ADMIN_PLATFORM_ROLES.has(platformRoleValue)) {
    return 'officer';
  }

  if (CALLER_ROLES.has(roleValue)) {
    return 'caller';
  }

  // Check for AI officer assistant: name ends with "(AI)" e.g., "Andrew Kelly (AI)"
  if (name && name.endsWith('(AI)')) {
    return 'officer';
  }

  if (identityValue.includes('__off_')) {
    return 'officer';
  }

  if (identityValue.includes('__cit_')) {
    return 'caller';
  }

  if (identityValue.includes('officer') || nameValue.includes('officer')) {
    return 'officer';
  }

  if (identityValue.includes('caller') || nameValue.includes('caller') || nameValue.includes('citizen')) {
    return 'caller';
  }

  return 'unknown';
}

export function isOfficerParticipant(args: Parameters<typeof getParticipantRole>[0]): boolean {
  return getParticipantRole(args) === 'officer';
}

export function isCallerParticipant(args: Parameters<typeof getParticipantRole>[0]): boolean {
  return getParticipantRole(args) === 'caller';
}

/**
 * Detects if a participant is the officer-assist TTS agent.
 * Checks identity string, parsed metadata `type` field, and raw metadata as fallback.
 */
export function isOfficerAssistParticipant({
  identity,
  name,
  metadata,
}: {
  identity?: string | null;
  name?: string | null;
  metadata?: string | null;
  // Plumbed by live callers during Phase A; legacy helper behavior is unchanged.
  kind?: unknown;
  attributes?: Record<string, string> | null;
}): boolean {
  const identityLower = (identity || '').toLowerCase();

  if (identityLower.includes('officer-assist') || identityLower.includes('officerassist')) {
    return true;
  }

  const parsed = parseParticipantMetadata(metadata);
  if (parsed?.type === 'officer-assist' || parsed?.isOfficerAssist) {
    return true;
  }

  const metadataLower = (metadata || '').toLowerCase();
  return metadataLower.includes('officer-assist');
}

/**
 * #632 — the ONLY sound way to decide "did a trusted officer send this?".
 *
 * Do NOT use `getParticipantRole` for a trust decision. It is correct for display and
 * wrong for authorization, because three of its five rungs are attacker-controlled and
 * all three are evaluated BEFORE the one that is not:
 *
 *   1. metadata.role / metadata.platform_role  -> 'officer'   ❌ human tokens carry
 *                                                                canUpdateOwnMetadata: true
 *   2. metadata.role in CALLER_ROLES           -> 'caller'    ❌ same
 *   3. name.endsWith('(AI)')                   -> 'officer'   ❌ name is a request parameter
 *                                                                supplied at token mint
 *   4. identity.includes('__off_')             -> 'officer'   ✅ server-derived, signed
 *   5. identity/name .includes('officer')      -> 'officer'   ❌ the name half is forgeable
 *
 * Only rung 4's input is trustworthy: `connection-details` mints
 * `${participantName}__${rolePrefix}_${postfix}` with `rolePrefix = isOfficer ? 'off' : 'cit'`
 * computed from the caller's DB roles, and identity is fixed in the signed token — a
 * participant cannot change it.
 *
 * This parses that suffix STRUCTURALLY rather than with `includes('__off_')`, because the
 * participant name is caller-supplied and could contain the delimiter itself. A caller
 * joining as `evil__off_x` gets the identity `evil__off_x__cit_abc`, which
 * `includes('__off_')` would accept and this function rejects: the role prefix is the
 * segment after the LAST `__`, which is `cit_abc`.
 *
 * Fails closed on anything it does not understand.
 */
export function isTrustedOfficerIdentity(identity?: string | null): boolean {
  if (typeof identity !== 'string' || identity.length === 0) return false;
  const separator = identity.lastIndexOf('__');
  if (separator === -1) return false;
  const suffix = identity.slice(separator + 2).toLowerCase();
  return /^off_/.test(suffix);
}

/**
 * #632 — the agent-side counterpart of `isTrustedOfficerIdentity`.
 *
 * Several receive paths apply state whose legitimate sender is a dispatched backend
 * agent (officer-assist), not a human officer — `auto_response_status`, officer-speech
 * playout confirmations, and the action-state text streams. Gating those on `__off_`
 * would reject the real sender, so they need their own primitive.
 *
 * `isOfficerAssistParticipant` is NOT that primitive, despite the name:
 *
 *   - it falls back to `metadata`, which human tokens can rewrite
 *     (`canUpdateOwnMetadata: true`); and
 *   - its identity rung is a plain substring match, so a caller who simply joins with
 *     the display name `officer-assist` produces the identity
 *     `officer-assist__cit_abc` and satisfies it.
 *
 * It remains fine for display. It must not decide trust.
 *
 * What IS unforgeable is the participant KIND. LiveKit assigns it server-side from how
 * the participant entered the room: humans minted through `connection-details` are
 * `STANDARD`; agents brought in with `AgentDispatchClient.createDispatch()` are `AGENT`.
 * Nothing a participant sends can change their own kind, so this is the strongest
 * discriminator available on the client.
 *
 * Scope, stated honestly: this proves "a dispatched agent sent it", not "officer-assist
 * specifically sent it". That closes the reported threat — a caller or other in-room
 * participant publishing on backend control topics — because no human participant can
 * ever be `AGENT`. Distinguishing one agent from another additionally requires a
 * server-minted agent identity, which does not exist yet; adding one belongs at the
 * dispatch boundary, not in a client-side guess.
 *
 * Fails closed: an undefined participant, an undefined kind, or any non-AGENT kind is
 * rejected.
 */
export function isTrustedAgentSender(participant?: { kind?: unknown } | null): boolean {
  if (!participant) return false;
  // ParticipantInfo_Kind.AGENT === 4. Compared numerically rather than by importing the
  // protocol enum so this stays usable from plain unit tests and from the `any`-typed
  // LiveKit data callbacks, where `kind` arrives as a number.
  return participant.kind === 4;
}
